import { AsyncData } from "#app";
import { FetchError } from "ofetch";
import {
  CartRelatedProductsResponse,
  CartResponse,
  CheckoutData,
  CheckoutResponse,
  PaypalOrderResponse,
  CartPaymentMethodsData,
  AddressData,
  shippingRatesByAddress,
  PaypalOrderDataResponse,
  StartExpressPaymentPayload,
  StartExpressPaymentPostResponse,
  updatePayPalOrderShippingMethod,
  expressCheckoutUpdateShippingResponse,
  ShippingMethodsResponse,
  SignInData,
} from "ecom-types";
import { useFetchInstance } from "~/api";
export const cartApi = {
  /**
   * Get cart
   *
   * @link https://dev-opr.diamondnexus.com/apidoc/#api-Cart-GetApiV1Carts
   * @returns {*}
   * @param accessToken
   * @param uniqueId
   * @param getCartByCartID
   */

  getCart(
    accessToken: SignInData["access_token"] | null,
    uniqueId?: string | null,
    getCartByCartID?: boolean,
  ) {
    const params: { cart_unique_id?: string } = {};
    if ((getCartByCartID && uniqueId) || (uniqueId && !accessToken)) {
      params.cart_unique_id = uniqueId as string;
    }
    return useFetchInstance<CartResponse>("/carts", { params });
  },
  /**
   * Create cart item
   *
   * @link https://dev-opr.diamondnexus.com/apidoc/#api-Cart-PostApiV1CartsItems
   * @returns {*}
   * @param data
   */
  addToCart(data: any) {
    return useFetchInstance<CartResponse>("/carts/items", {
      method: "POST",
      body: data,
    });
  },

  /**
   * Update cart item
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PatchApiV1CartsItemsId
   * @param id
   * @param data
   * @returns {*}
   */
  updateCartItem(id: number, data: any) {
    return useFetchInstance<CartResponse>(`/carts/items/${id}`, {
      method: "PATCH",
      body: data,
    });
  },

  /**
   * Delete cart item
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-DeleteApiV1CartsItemsId
   * @param id
   * @param data
   * @returns {*}
   */
  deleteCartItem(id: number, data: any) {
    return useFetchInstance<CartResponse>(`/carts/items/${id}`, {
      method: "DELETE",
      body: data,
      key: String(id),
    });
  },

  /**
   * Bind gift card or discount to cart
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PostApiV1CartsBindGiftCard
   * @returns {*}
   * @param data
   */
  bindDiscount(data: any) {
    return useFetchInstance<CartResponse>(`/carts/bind-gift-card`, {
      method: "POST",
      body: data,
    });
  },

  /**
   * Bind gift card or discount to cart
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PostApiV1CartsUnbindGiftCard
   * @returns {*}
   * @param data
   */
  unbindDiscount(data: { cart_unique_id: string; gift_card_code: string }) {
    return useFetchInstance<CartResponse>(`/carts/unbind-gift-card`, {
      method: "POST",
      body: data,
    });
  },

  /**
   * Storing checkout data
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PostApiV1CartsCheckout
   * @param data
   * @returns {*}
   */
  checkout(data: CheckoutData) {
    return useFetchInstance<CheckoutResponse>(`/carts/checkout`, {
      method: "POST",
      body: data,
    });
  },
  /**
   * Paypal order
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PostApiV1CartsCheckoutPaypalOrder
   * @param data
   * @returns {*}
   */
  paypalOrder(data: { cart_unique_id: string }) {
    return useFetchInstance<PaypalOrderResponse>(
      `/carts/checkout/paypal/order`,
      {
        method: "POST",
        body: data,
      },
    );
  },

  /**
   * Get cart related product
   *
   * @link https://dev-opr.diamondnexus.com/apidoc/#api-Cart-GetApiV1CartsCart_unique_idRelatedProducts
   * @returns {*}
   */

  getCartRelatedProducts(
    uniqueId: string | null,
  ):
    | AsyncData<CartRelatedProductsResponse, FetchError<any> | null>
    | { data: null } {
    if (!uniqueId) return { data: null };
    return useFetchInstance<CartRelatedProductsResponse>(
      `/carts/${uniqueId}/related-products`,
    );
  },

  /**
   * Start express payment
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/index.html#api-Cart-PostApiV1CartsStartExpressPayment
   * @returns {*}
   */
  //  /carts/checkout/express /carts/start-express-payment
  startExpressPayment(payload: StartExpressPaymentPayload) {
    return useFetchInstance<StartExpressPaymentPostResponse | CheckoutResponse>(
      "/carts/checkout/express",
      {
        method: "POST",
        body: payload,
      },
    );
  },

  /**
   * Shipping rates by address
   *
   * @link
   * @returns {*}
   */
  shippingRatesByAddress(payload: shippingRatesByAddress) {
    return useFetchInstance<ShippingMethodsResponse>(
      "/shipping-rates-by-address",
      {
        method: "POST",
        body: payload,
      },
    );
    // const config = useRuntimeConfig();
    // return $fetch("/shipping-rates-by-address", {
    //   method: "POST",
    //   body: payload,
    //   baseURL: config.public.apiBase,
    // });
  },

  /**
   * Update payment method
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PatchApiV1CartsPaymentMethod
   * @returns {*}
   */
  //  /carts/checkout/express /carts/start-express-payment
  updatePaymentMethod(payload: CartPaymentMethodsData) {
    return useFetchInstance<any>("/carts/payment-method", {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * Update PayPal Order Shipping Address
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PatchApiV1CartsCheckoutPaypalShippingAddress
   * @param data
   * @returns {*}
   */
  updatePayPalOrderShippingAddress(data: {
    cart_unique_id: string;
    paypal_order_id: string;
    shipping_address: AddressData;
  }) {
    return useFetchInstance<PaypalOrderResponse>(
      `/carts/checkout/paypal/shipping-address`,
      {
        method: "PATCH",
        body: data,
      },
    );
  },
  /**
   * Update PayPal Order Shipping Method
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PatchApiV1CartsCheckoutPaypalShippingMethod
   * @param data
   * @returns {*}
   */
  updatePayPalOrderShippingMethod(data: {
    cart_unique_id: string;
    paypal_order_id: string;
    shipping_method: string | number;
  }) {
    return useFetchInstance<updatePayPalOrderShippingMethod>( // TODO
      `/carts/checkout/paypal/shipping-method`,
      {
        method: "PATCH",
        body: data,
      },
    );
  },

  /**
   * Express checkout update shipping
   *
   * @link https://dev-opr.diamondnexus.com/public/apidoc/#api-Cart-PatchApiV1CartsCheckoutExpressShipping
   * @param data
   * @returns {*}
   */
  expressCheckoutUpdateShipping(data: {
    cart_unique_id: string;
    shipping_address: AddressData;
    shipping_method: string | number;
  }) {
    return useFetchInstance<expressCheckoutUpdateShippingResponse>(
      `/carts/checkout/express/shipping`,
      {
        method: "PATCH",
        body: data,
      },
    );
  },
  /**
   * Paypal order data express
   *
   * @link
   * @returns {*}
   * @param cart_unique_id
   * @param paypal_order_id
   */

  getPaypalOrderExpress(cart_unique_id: string, paypal_order_id: string) {
    return useFetchInstance<PaypalOrderDataResponse>(
      `/carts/checkout/paypal/order`,
      {
        method: "GET",
        query: {
          cart_unique_id,

          paypal_order_id,
        },
      },
    );
  },
  /**
   * Start checkout event
   *
   * @link
   * @returns {*}
   * @param cart_unique_id
   */

  sendStartCheckoutEvent(cart_unique_id: string) {
    return useFetchInstance<PaypalOrderDataResponse>(
      `/carts/${cart_unique_id}/start-checkout`,
      {
        method: "PATCH",
      },
      { skipErrors: [404, 500, 400] },
    );
  },
};
